import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import i18next from 'i18next';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { openGenericModal, refreshGenericModal } from '@actions/modal';
import { showConfirmationMessage, showErrorMessage } from '@actions/messageconfirmation';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { DEFAULT_FULL_INVENTORY_TEMPLATE } from '@commons/constants/dropdown';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getIsCentralMode } from '@commons/utils/localStorage';
import ItemsWithTitleSubtitle from '@commons/DropdownItems/ItemsWithTitleSubtitle';

import { getCentralKitchenStores, getSalesPointStores } from '@selectors/stores';
import { getClientInfo } from '@selectors/client';

import { storeSupplierProductMapping as sspmService } from '@services/storeSupplierProductMapping';
import clientService from '@services/client';
import orderService from '@services/order';

import { getCreateInitialInventoryModalConfig } from './modalConfiguration';

const CreateInitialInventoryModal = (props) => {
  const {
    handleCreateInitialInventory,
    resetCreateInitialInventoryModal,
    openGenericModal,
    refreshGenericModal,
    client: { storeName },
    stores,
    modal,
    showMessage,
    showErrorMessage,
  } = props;

  const [selectedStores, setSelectedStores] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [shouldRestrictSupplierProducts, setShouldRestrictSupplierProducts] = useState(false);
  const [selectedSupplierProducts, setSelectedSupplierProducts] = useState([]);
  const [focusedMonth, setFocusedMonth] = useState(moment());
  const [forbiddenDates, setForbiddenDates] = useState([]);
  const [storeILTs, setStoreILTs] = useState([DEFAULT_FULL_INVENTORY_TEMPLATE()]);
  const [selectedStoreILT, setSelectedStoreILT] = useState(DEFAULT_FULL_INVENTORY_TEMPLATE());

  const [translatedStoreName, setTranslatedStoreName] = useState(null);

  const getDatesWithInventoriesForMonth = async (storeIds, focusedMonth) => {
    try {
      const startDate = moment(focusedMonth)
        .startOf('month')
        .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);
      const endDate = moment(focusedMonth)
        .endOf('month')
        .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);

      const { dates } = await orderService.getInventoryForStores(
        storeIds,
        startDate,
        endDate,
        showMessage,
        'stock',
      );

      const storesInventoriesDates = dates.map((date) => moment(date));

      setForbiddenDates(storesInventoriesDates);
    } catch {
      showErrorMessage(i18next.t('STOCKS.STOCKS.LOADING_FAILURE'));
    }
  };

  const getSSPMByStoreIds = async (storeIds) => {
    try {
      const supplierProducts = await sspmService.getByStoreIds(
        storeIds,
        // SP IDs to ignore
        [],
        // Filter only those available in stock
        true,
      );

      const formattedSupplierProducts = supplierProducts.map((supplierProduct) => ({
        ...supplierProduct,
        renderValue: () =>
          ItemsWithTitleSubtitle({
            title: supplierProduct.name,
            subtitle: get(supplierProduct, 'supplierName', ''),
          }),
      }));

      setSupplierProducts(formattedSupplierProducts);
      setSelectedSupplierProducts(supplierProducts);
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.FETCH_ERROR'));
    }
  };

  const getInventoryListTemplatesForStores = async (storeIds) => {
    try {
      const templates = await clientService.getInventoryListTemplates({
        withMappings: true,
        filterByUserCatalog: false,
        storeIds,
      });

      const formattedTemplates = (templates || []).map(
        ({ id, name, storeId, supplierProducts }) => ({
          id,
          name,
          storeId,
          supplierProductIds: (supplierProducts || []).map(
            ({ id: spRowId, supplierProductId }) => supplierProductId || spRowId,
          ),
        }),
      );

      setStoreILTs([DEFAULT_FULL_INVENTORY_TEMPLATE(), ...formattedTemplates]);
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIER_PRODUCTS.FETCH_INVENTORY_LIST_TEMPLATES_ERROR'));
      setStoreILTs([DEFAULT_FULL_INVENTORY_TEMPLATE()]);
    }
  };

  useEffect(() => {
    const storeNameTranslation = getClientStoreNameTranslation(storeName, true);
    setTranslatedStoreName(storeNameTranslation);
  }, []);

  useEffect(() => {
    setSelectedDate(null);

    if (isEmpty(selectedStores)) {
      setSupplierProducts([]);
      setSelectedSupplierProducts([]);
      setStoreILTs([DEFAULT_FULL_INVENTORY_TEMPLATE()]);
      setSelectedStoreILT(DEFAULT_FULL_INVENTORY_TEMPLATE());
      return;
    }

    const storeIds = selectedStores.map(({ id }) => id);

    getSSPMByStoreIds(storeIds);
    getInventoryListTemplatesForStores(storeIds);
  }, [selectedStores]);

  useEffect(() => {
    const defaultTemplateId = DEFAULT_FULL_INVENTORY_TEMPLATE().id;

    if (selectedStoreILT.id === defaultTemplateId) {
      return;
    }

    const isSelectedStoreILTStillAvailable = storeILTs.some(({ id }) => id === selectedStoreILT.id);

    if (!isSelectedStoreILTStillAvailable) {
      setSelectedStoreILT(DEFAULT_FULL_INVENTORY_TEMPLATE());
    }
  }, [storeILTs]);

  useEffect(() => {
    if (!isEmpty(selectedStores)) {
      const storeIds = selectedStores.map(({ id }) => id);

      getDatesWithInventoriesForMonth(storeIds, focusedMonth);
    }
  }, [selectedStores, focusedMonth]);

  useEffect(() => {
    const isModalOpened = get(modal, 'GenericModalBool', false);

    const params = getCreateInitialInventoryModalConfig({
      handleCreateInitialInventory,
      resetCreateInitialInventoryModal,
      // Stores
      stores,
      selectedStores,
      setSelectedStores,
      translatedStoreName,
      // Supplier Products
      supplierProducts,
      selectedSupplierProducts,
      setSelectedSupplierProducts,
      shouldRestrictSupplierProducts,
      setShouldRestrictSupplierProducts,
      // Inventory List Templates
      storeILTs,
      selectedStoreILT,
      setSelectedStoreILT,
      // Date
      selectedDate,
      setSelectedDate,
      forbiddenDates,
      focusedMonth,
      setFocusedMonth,
    });

    if (isModalOpened) {
      refreshGenericModal(params);
      return;
    }

    openGenericModal(params);
  }, [
    selectedStores,
    supplierProducts,
    selectedSupplierProducts,
    shouldRestrictSupplierProducts,
    selectedDate,
    focusedMonth,
    forbiddenDates,
    translatedStoreName,
    storeILTs,
    selectedStoreILT,
  ]);

  return <div />;
};

const mapStateToProps = (state) => ({
  stores: getIsCentralMode()
    ? getCentralKitchenStores(state.baseReducer.activeStores)
    : getSalesPointStores(state.baseReducer.activeStores),
  modal: state.modalReducer,
  client: getClientInfo(state.baseReducer.user),
});

const mapDispatchToProps = (dispatch) => ({
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
  showMessage: (message, type) => {
    dispatch(showConfirmationMessage(message, type));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

CreateInitialInventoryModal.propTypes = {
  handleCreateInitialInventory: PropTypes.func, // passed by InventoryListView.js, to perform the API call
  resetCreateInitialInventoryModal: PropTypes.func, // passed by InventoryListView.js, to reset its state
  openGenericModal: PropTypes.func, // passed by redux
  refreshGenericModal: PropTypes.func, // passed by redux
  stores: PropTypes.array, // passed by redux
  modal: PropTypes.array, // passed by redux
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateInitialInventoryModal);
